import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BackButton, IdControl, ImageViewer } from '@shared-ui/components';
import { LatexText, QuestionFigures } from '@shared-ui/latex';
import { useModules } from '@shared-ui/reference-data';
import { QuestionHistoryPanel } from '@shared-ui/question-history';
import {
    questionQualityApi, LEDGER_QUERY_KEY, patchLedgerQuestionResolution,
} from '../../../api/questionQualityApi';
import { questionApi } from '../../../api/questionApi';
import type { QualityVerdict, Resolution, LedgerResponse } from '../../../api/questionQualityApi';
import type { Question } from '../../../types';
import { sevColor, sevRank, verdictColor } from './verdictDisplay';
import { ResolutionBadge, ResolutionEditor } from './ResolutionEditor';
import { AiFixCard } from './AiFixCard';
import { FindingHistory } from './FindingHistory';
import './QuestionQuality.css';

/**
 * Everything judged about ONE question, on its own page.
 *
 * This was an expanding row inside the matrix. The matrix is ten narrow columns
 * beside a stem, so the expansion inherited that width: rationales — which are
 * paragraphs, and the actual finding — were squeezed into a column, and the
 * resolution control next to them had barely room for its select, let alone the
 * comment box that is the point of it.
 *
 * A page also makes a finding ADDRESSABLE. "Look at this question" is now a URL
 * that survives a reload, and the By Rule drill-down links to the same place
 * rather than growing a second copy of the same panel.
 *
 * It reads its own data (`by-question`) instead of taking the row it came from,
 * so arriving by link works exactly like arriving by click.
 */

export function QuestionFindings() {
    const { questionId = '' } = useParams();
    const [params] = useSearchParams();
    /** Only a partition hint for the server, and only useful when the question
     *  has been deleted. It is whatever filter the reader happened to have set,
     *  so it is capable of naming a different subject than the question's —
     *  never show it, and never let it decide anything. */
    const subjectHint = params.get('subject') || undefined;
    const { moduleNameMap } = useModules();

    const [data, setData] = useState<Awaited<ReturnType<typeof questionQualityApi.byQuestion>> | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // The full record — answer, solutions, options — fetched separately from
    // the verdicts. A judging rule only needs the stem to name a finding; a
    // human deciding whether that finding is REAL usually needs the key too.
    // Best-effort: a deleted question still shows its findings even though
    // this fetch 404s, so its failure is swallowed rather than surfacing as
    // the page's own error.
    const [question, setQuestion] = useState<Question | null>(null);
    const [showAnswer, setShowAnswer] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            setData(await questionQualityApi.byQuestion(questionId, subjectHint));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load findings');
            setData(null);
        } finally { setLoading(false); }
        setQuestion(await questionApi.getQuestionById(questionId).catch(() => null));
    }, [questionId, subjectHint]);

    useEffect(() => { void load(); }, [load]);
    useEffect(() => { setShowAnswer(false); }, [questionId]);

    /** Findings first, worst first. A passing check is shown too — knowing a rule
     *  RAN and was happy is what stops the same question being re-examined. */
    const rank = (v: QualityVerdict) =>
        (v.verdict === 'fail' ? 10 + sevRank[v.severity] : v.verdict === 'unsure' ? 5 : 0);
    const verdicts = [...(data?.verdicts ?? [])].sort((a, b) => rank(b) - rank(a));
    const issues = verdicts.filter(v => v.verdict !== 'pass');
    /**
     * A pass that carries a decision or a history is not a rule id in a list.
     *
     * `verified-fixed` is the case: the rule passes BECAUSE somebody fixed the
     * question and a later sweep re-judged the edit. That is the one passing
     * check on the page anybody wants to read, and it is also the only record
     * that this question was ever wrong.
     */
    const passes = verdicts.filter(v => v.verdict === 'pass');
    const settled = passes.filter(v => v.resolution || v.priorFindings?.length);
    const plainPasses = passes.filter(v => !v.resolution && !v.priorFindings?.length);

    const queryClient = useQueryClient();

    const applyResolution = (verdictId: string, ruleId: string, next: Resolution | undefined) => {
        setData(prev => (prev ? {
            ...prev,
            verdicts: prev.verdicts.map(v => (v.id === verdictId ? { ...v, resolution: next } : v)),
        } : prev));

        // Patch the ONE row behind this page in every cached list, rather than
        // leaving it stale until the list reloads. `setQueriesData` (plural,
        // partial key) rather than `setQueryData`: the reader could have
        // arrived here from any of several cached (subject, filter, page)
        // combinations, and any of them holding this row should reflect the
        // edit — not just whichever one they happen to land back on.
        queryClient.setQueriesData<LedgerResponse>({ queryKey: [LEDGER_QUERY_KEY], exact: false }, old => {
            if (!old) return old;
            const idx = old.questions.findIndex(q => q.questionId === questionId);
            if (idx === -1) return old; // this question isn't on that cached page
            const questions = [...old.questions];
            questions[idx] = patchLedgerQuestionResolution(questions[idx], ruleId, next);
            return { ...old, questions };
        });
    };

    return (
        <div className="submissions-page">
            {/* Back to the LIST AS IT WAS. `from` carries the filters, the page
                and the subject the reader left behind; without it they returned
                to an unfiltered page one, which is the same as losing their
                place. A link arrived at cold falls back to the plain tab. */}
            <BackButton
                to={params.get('from')
                    ? `/monitoring${params.get('from')}`
                    : '/monitoring?tab=question-quality&qq=by-question'}
                label="Question Quality"
            />

            <div className="page-header">
                <h1>Question findings</h1>
                <p className="qq-dim">
                    <IdControl value={questionId} container="questions" />
                    {data?.questionType ? ` · ${data.questionType}` : ''}
                    {data?.moduleId ? ` · ${moduleNameMap.get(data.moduleId) ?? data.moduleId}` : ''}
                    {/* The question's own subject, not the URL's. */}
                    {data?.subject ? ` · ${data.subject}` : ''}
                </p>
            </div>

            {loading && <p className="qq-dim">Loading…</p>}
            {error && <p className="qq-warn">{error}</p>}

            {data && (
                <div className="qq-findings-layout">
                    {/* Left: every finding, and what to do about each — the
                        working column, so it comes first in reading and DOM
                        order (keyboard/screen-reader users land here, not on
                        the reference panel). */}
                    <div className="qq-findings-col">
                        <section className="settings-section">
                            <h2>Findings <span className="qq-dim">({issues.length})</span></h2>
                            {/* "Nothing ran" and "everything passed" are opposite
                                answers to "has this been checked?", and they used to
                                print the same sentence. */}
                            {verdicts.length === 0 && (
                                <p className="qq-dim">
                                    Not checked yet — no rule has been run against this question.
                                    Run the worklist → judge → record flow (<code>scripts/quality</code>).
                                </p>
                            )}
                            {verdicts.length > 0 && issues.length === 0 && (
                                <p className="qq-dim">Every check that ran was happy with this question.</p>
                            )}
                            {issues.map(v => (
                                <article key={v.id} className="qq-finding">
                                    <header>
                                        <b>{v.ruleId}</b>
                                        <span style={{ color: v.verdict === 'fail' ? sevColor[v.severity] : verdictColor[v.verdict] }}>
                                            {v.verdict === 'fail' ? `fail · ${v.severity}` : v.verdict}
                                        </span>
                                        <ResolutionBadge status={v.resolution?.status ?? 'active'} />
                                        <span className="qq-dim">{new Date(v.checkedAt).toLocaleDateString()}</span>
                                    </header>
                                    {/* The paragraph this page exists to make readable. */}
                                    <p className="qq-finding-why"><LatexText text={v.rationale} /></p>
                                    {/* Between the finding and the control that
                                        closes it — the order a reviewer works in:
                                        what was wrong, what the AI did about it,
                                        then their decision. */}
                                    {v.aiFix && <AiFixCard fix={v.aiFix} />}
                                    {/* Before the control, not after: "this was
                                        fixed once and came back" is context for
                                        the decision, not a footnote to it. */}
                                    <FindingHistory
                                        prior={v.priorFindings ?? []}
                                        dropped={v.priorFindingsDropped}
                                    />
                                    <ResolutionEditor
                                        subject={v.subject}
                                        questionId={v.questionId}
                                        ruleId={v.ruleId}
                                        resolution={v.resolution}
                                        aiFixable={v.aiFixable}
                                        onSaved={next => applyResolution(v.id, v.ruleId, next)}
                                    />
                                </article>
                            ))}
                        </section>

                        {/* The before/after the AI-fix card points at, and the
                            history of every other edit besides. Read-only: restore
                            belongs to the question editor, not to a page whose job
                            is to decide whether one finding is settled.

                            The panel and its API client both already existed and had
                            no caller anywhere — the version history was built and
                            never surfaced. */}
                        {data.questionText && (
                            <section className="settings-section">
                                <QuestionHistoryPanel
                                    questionId={questionId}
                                    historyApi={{
                                        list: questionApi.getRevisions,
                                        get: questionApi.getRevision,
                                        restore: questionApi.restoreRevision,
                                    }}
                                    readOnly
                                />
                            </section>
                        )}

                        {settled.length > 0 && (
                            <section className="settings-section">
                                <h2>Settled <span className="qq-dim">({settled.length})</span></h2>
                                {settled.map(v => (
                                    <article key={v.id} className="qq-finding qq-finding--settled">
                                        <header>
                                            <b>{v.ruleId}</b>
                                            <span style={{ color: verdictColor.pass }}>pass</span>
                                            {v.resolution && <ResolutionBadge status={v.resolution.status} />}
                                            <span className="qq-dim">{new Date(v.checkedAt).toLocaleDateString()}</span>
                                        </header>
                                        {v.resolution && (
                                            <p className="qq-dim">
                                                {v.resolution.comment ? `“${v.resolution.comment}” — ` : ''}
                                                {v.resolution.byName ?? v.resolution.by?.slice(0, 8) ?? 'someone'}
                                                {v.resolution.verifiedAt
                                                    ? `, confirmed by the sweep on ${new Date(v.resolution.verifiedAt).toLocaleDateString()}`
                                                    : `, ${new Date(v.resolution.at).toLocaleDateString()}`}
                                            </p>
                                        )}
                                        {v.aiFix && <AiFixCard fix={v.aiFix} />}
                                        <FindingHistory
                                            prior={v.priorFindings ?? []}
                                            dropped={v.priorFindingsDropped}
                                        />
                                        {/* Still writable: a confirmed fix a
                                            reader disagrees with has to be
                                            reopenable, or the ledger's last word
                                            belongs to the sweep. */}
                                        <ResolutionEditor
                                            subject={v.subject}
                                            questionId={v.questionId}
                                            ruleId={v.ruleId}
                                            resolution={v.resolution}
                                            aiFixable={v.aiFixable}
                                            onSaved={next => applyResolution(v.id, v.ruleId, next)}
                                        />
                                    </article>
                                ))}
                            </section>
                        )}

                        {plainPasses.length > 0 && (
                            <section className="settings-section">
                                <h2>Checks that passed <span className="qq-dim">({plainPasses.length})</span></h2>
                                <p className="qq-dim qq-passlist">{plainPasses.map(v => v.ruleId).join(' · ')}</p>
                            </section>
                        )}
                    </div>

                    {/* Right: the record itself, for judging whether a finding is
                        real — pinned so it stays in view as the reader works
                        down the finding list on the left. Read-only by design:
                        editing (or restoring an old version) means leaving for
                        the full question tab, where those actions actually
                        live. */}
                    <div className="qq-findings-col qq-findings-col-question">
                        <section className="settings-section qq-stem-section">
                            {data.questionText ? (
                                <>
                                    <LatexText text={data.questionText} figures={question?.figures} />

                                    {question?.questionDiagramUrl && (
                                        <ImageViewer
                                            src={question.questionDiagramUrl}
                                            alt="Question diagram"
                                            className="qq-qimage"
                                        />
                                    )}
                                    <QuestionFigures text={data.questionText} figures={question?.figures} className="qq-qimage" />

                                    {question?.options && question.options.length > 0 && (
                                        <div className="qq-options">
                                            {question.options.map((opt, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`qq-option${showAnswer && opt.isCorrect ? ' is-correct' : ''}`}
                                                >
                                                    <span className="qq-option-label">{opt.label.toUpperCase()}.</span>
                                                    <span className="qq-option-text"><LatexText text={opt.text} /></span>
                                                    {opt.imageUrl && (
                                                        <ImageViewer src={opt.imageUrl} alt={opt.label} className="qq-option-image" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {question && (
                                        <button
                                            type="button"
                                            className="qq-expand-toggle"
                                            aria-expanded={showAnswer}
                                            onClick={() => setShowAnswer(s => !s)}
                                        >
                                            {showAnswer ? '▾ Hide' : '▸ Show'} correct answer &amp; solution
                                        </button>
                                    )}

                                    {showAnswer && question && (
                                        <div className="qq-answer-block">
                                            {question.correctAnswer && (
                                                <p className="qq-answer">
                                                    <strong>Correct answer:</strong> <LatexText text={question.correctAnswer} />
                                                </p>
                                            )}
                                            {question.solutionImageUrl && (
                                                <ImageViewer
                                                    src={question.solutionImageUrl}
                                                    alt="Solution"
                                                    className="qq-qimage"
                                                />
                                            )}
                                            {(question.solutions ?? []).map((s, idx) => (
                                                <div key={idx} className="qq-solution">
                                                    <p className="qq-dim">
                                                        {s.style ?? 'solution'}{s.label ? ` · ${s.label}` : ''}
                                                    </p>
                                                    <LatexText text={s.steps} />
                                                </div>
                                            ))}
                                            {!question.correctAnswer && !(question.solutions ?? []).length && (
                                                <p className="qq-dim">No answer or solution recorded.</p>
                                            )}
                                        </div>
                                    )}

                                    {/* The resolution they came here to write is on
                                        THIS page — a new tab so sending them to edit
                                        doesn't lose a comment half-typed. A `Link`
                                        rather than a bare anchor, so the tenant prefix
                                        in the router's basename survives. */}
                                    <Link
                                        className="qq-fullq"
                                        to={`/questions?id=${encodeURIComponent(questionId)}${data.teacherId ? '&source=teacher' : ''}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >Open full question to edit ↗</Link>
                                </>
                            ) : (
                                <p className="qq-dim">This question no longer exists; its verdicts are kept.</p>
                            )}
                        </section>
                    </div>
                </div>
            )}
        </div>
    );
}
